import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useTranslations } from 'use-intl';
import { DocumentRecord } from '@/pages/CustomerSupport/types';
import { formatDateTime } from '@/utils/utils';
import PlanADataSection from './CustomerDocuments/PlanADataSection';
import PlanBDataSection from './CustomerDocuments/PlanBDataSection';

interface CustomerDocumentsSectionProps {
	documentRecord: DocumentRecord | null;
	isExpanded?: boolean;
}

const CustomerDocumentsSection: React.FC<CustomerDocumentsSectionProps> = ({
	documentRecord,
	isExpanded: isExpandedProp = true
}) => {
	const t = useTranslations('SupportClients');
	const [isLocalExpanded, setIsLocalExpanded] = useState(true);
	const isExpanded = isExpandedProp && isLocalExpanded;

	const getVerificationStatusColor = (status: string) => {
		const colors: Record<string, string> = {
			pending: 'bg-yellow-50 text-yellow-600',
			complete: 'bg-green-50 text-green-600',
			needs_review: 'bg-orange-50 text-orange-600'
		};
		return colors[status] || 'bg-gray-50 text-gray-600';
	};

	const getVerificationStatusLabel = (status: string) => {
		const labels: Record<string, string> = {
			pending: t('Pending'),
			complete: t('Complete'),
			needs_review: t('NeedsReview')
		};
		return labels[status] || status;
	};

	if (!documentRecord) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
				<div
					className="flex items-center justify-between cursor-pointer mb-4"
					onClick={() => setIsLocalExpanded(!isLocalExpanded)}
				>
					<h2 className="text-lg font-semibold">{t('CustomerDocuments')}</h2>
					{isExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
				{isExpanded && <p className="text-gray-500 text-center py-6">{t('NoDocumentData')}</p>}
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
			<div
				className="flex items-center justify-between cursor-pointer mb-4"
				onClick={() => setIsLocalExpanded(!isLocalExpanded)}
			>
				<h2 className="text-lg font-semibold">{t('CustomerDocuments')}</h2>
				{isExpanded ? (
					<ChevronUp size={20} className="text-gray-500" />
				) : (
					<ChevronDown size={20} className="text-gray-500" />
				)}
			</div>
			{isExpanded && (
				<div className="flex flex-col gap-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm text-gray-500 mb-1">{t('PlatformPlan')}</label>
							<p className="text-sm font-medium">
								{t('Plan')} {documentRecord.platform_plan}
							</p>
						</div>
						<div>
							<label className="block text-sm text-gray-500 mb-1">{t('VerificationStatus')}</label>
							<span
								className={`inline-block px-3 py-1 rounded-xl text-xs font-medium ${getVerificationStatusColor(documentRecord.verification_status)}`}
							>
								{getVerificationStatusLabel(documentRecord.verification_status)}
							</span>
						</div>
					</div>

					{documentRecord.plan_a_data && Object.keys(documentRecord.plan_a_data).length > 0 && (
						<PlanADataSection planAData={documentRecord.plan_a_data} />
					)}

					{documentRecord.plan_b_data && Object.keys(documentRecord.plan_b_data).length > 0 && (
						<PlanBDataSection planBData={documentRecord.plan_b_data} />
					)}

					<div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
						<div>
							<label className="block text-sm text-gray-500 mb-1">{t('CreatedAt')}</label>
							<p className="text-sm font-medium">{formatDateTime(documentRecord.created_at)}</p>
						</div>
						<div>
							<label className="block text-sm text-gray-500 mb-1">{t('UpdatedAt')}</label>
							<p className="text-sm font-medium">{formatDateTime(documentRecord.updated_at)}</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CustomerDocumentsSection;
